import React, { useState } from 'react';
import { Clock, Mail, Play, Edit, Trash2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { format, addDays, addHours } from 'date-fns';

interface ScheduledReport {
  id: string;
  name: string;
  template: string;
  schedule: {
    frequency: 'daily' | 'weekly' | 'monthly';
    time: string;
    dayOfWeek?: number; // 0-6 for weekly
    dayOfMonth?: number; // 1-31 for monthly
  };
  recipients: string[];
  format: 'pdf' | 'excel' | 'csv';
  isActive: boolean;
  lastRun?: Date;
  nextRun?: Date;
  runCount: number;
}

interface ScheduledReportsProps {
  reports: ScheduledReport[];
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onToggle: (id: string, active: boolean) => void;
  onRunNow: (id: string) => void;
  onCreate: (report: Omit<ScheduledReport, 'id' | 'runCount' | 'lastRun' | 'nextRun'>) => void;
}

const defaultReports: ScheduledReport[] = [
  {
    id: '1',
    name: 'Daily Fraud Summary',
    template: 'Daily Fraud Summary',
    schedule: {
      frequency: 'daily',
      time: '09:00'
    },
    recipients: ['team@company.com', 'management@company.com'],
    format: 'pdf',
    isActive: true,
    lastRun: new Date(Date.now() - 24 * 60 * 60 * 1000),
    nextRun: addHours(new Date(), 2),
    runCount: 45
  },
  {
    id: '2',
    name: 'Weekly Partner Analysis',
    template: 'Partner Performance Report',
    schedule: {
      frequency: 'weekly',
      time: '08:00',
      dayOfWeek: 1 // Monday
    },
    recipients: ['partners@company.com'],
    format: 'excel',
    isActive: true,
    lastRun: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    nextRun: addDays(new Date(), 2),
    runCount: 12
  },
  {
    id: '3',
    name: 'Monthly Executive Report',
    template: 'Monthly Executive Report',
    schedule: {
      frequency: 'monthly',
      time: '07:00',
      dayOfMonth: 1
    },
    recipients: ['executives@company.com'],
    format: 'pdf',
    isActive: true,
    nextRun: addDays(new Date(), 15),
    runCount: 3
  }
];

export function ScheduledReports({ 
  reports = defaultReports, 
  onEdit, 
  onDelete, 
  onToggle, 
  onRunNow, 
  onCreate 
}: ScheduledReportsProps) {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newReport, setNewReport] = useState<Partial<ScheduledReport>>({
    name: '',
    template: '',
    schedule: {
      frequency: 'daily',
      time: '09:00'
    },
    recipients: [],
    format: 'pdf',
    isActive: true
  });

  const formatNextRun = (date?: Date) => {
    if (!date) return 'Not scheduled';
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    
    if (diffMs < 0) return 'Overdue';
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `${format(date, 'MMM dd')} at ${format(date, 'HH:mm')}`;
    } else if (diffHours > 0) {
      return `Today at ${format(date, 'HH:mm')}`;
    } else {
      return 'Soon';
    }
  };

  const getScheduleDescription = (schedule: ScheduledReport['schedule']) => {
    const timeStr = schedule.time;
    
    switch (schedule.frequency) {
      case 'daily':
        return `Every day at ${timeStr}`;
      case 'weekly':
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayName = days[schedule.dayOfWeek || 0];
        return `Every ${dayName} at ${timeStr}`;
      case 'monthly':
        const dayNum = schedule.dayOfMonth || 1;
        const suffix = dayNum === 1 ? 'st' : dayNum === 2 ? 'nd' : dayNum === 3 ? 'rd' : 'th';
        return `${dayNum}${suffix} of every month at ${timeStr}`;
      default:
        return 'Custom schedule';
    }
  };

  const handleCreateReport = () => {
    if (newReport.name && newReport.template && newReport.schedule) {
      onCreate({
        name: newReport.name,
        template: newReport.template,
        schedule: newReport.schedule,
        recipients: newReport.recipients || [],
        format: newReport.format || 'pdf',
        isActive: newReport.isActive || true
      });
      setNewReport({
        name: '',
        template: '',
        schedule: {
          frequency: 'daily',
          time: '09:00'
        },
        recipients: [],
        format: 'pdf',
        isActive: true
      });
      setIsCreateDialogOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Scheduled Reports</h2>
          <p className="text-muted-foreground">
            Automated report generation and delivery
          </p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Schedule
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Scheduled Report</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="report-name">Report Name</Label>
                <Input
                  id="report-name"
                  value={newReport.name}
                  onChange={(e) => setNewReport(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter report name"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Template</Label>
                <Select
                  value={newReport.template}
                  onValueChange={(value) => setNewReport(prev => ({ ...prev, template: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Daily Fraud Summary">Daily Fraud Summary</SelectItem>
                    <SelectItem value="Weekly Analytics Report">Weekly Analytics Report</SelectItem>
                    <SelectItem value="Partner Performance Report">Partner Performance Report</SelectItem>
                    <SelectItem value="Monthly Executive Report">Monthly Executive Report</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Frequency</Label>
                  <Select
                    value={newReport.schedule?.frequency}
                    onValueChange={(value: 'daily' | 'weekly' | 'monthly') => 
                      setNewReport(prev => ({ 
                        ...prev, 
                        schedule: { ...prev.schedule!, frequency: value }
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={newReport.schedule?.time}
                    onChange={(e) => setNewReport(prev => ({ 
                      ...prev, 
                      schedule: { ...prev.schedule!, time: e.target.value }
                    }))}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Recipients (comma-separated)</Label>
                <Textarea
                  value={newReport.recipients?.join(', ')}
                  onChange={(e) => setNewReport(prev => ({ 
                    ...prev, 
                    recipients: e.target.value.split(',').map(email => email.trim()).filter(Boolean)
                  }))}
                  placeholder="team@company.com, management@company.com"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Format</Label>
                <Select
                  value={newReport.format}
                  onValueChange={(value: 'pdf' | 'excel' | 'csv') => 
                    setNewReport(prev => ({ ...prev, format: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">PDF Report</SelectItem>
                    <SelectItem value="excel">Excel Workbook</SelectItem>
                    <SelectItem value="csv">CSV Data</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateReport}>
                  Create Schedule
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {reports.map((report) => (
          <Card key={report.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold">{report.name}</h3>
                    <Badge variant={report.isActive ? 'default' : 'secondary'}>
                      {report.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                    <Badge variant="outline">
                      {report.format.toUpperCase()}
                    </Badge>
                  </div>
                  
                  <div className="text-sm text-muted-foreground space-y-1">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>{getScheduleDescription(report.schedule)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      <span>{report.recipients.join(', ')}</span>
                    </div>
                  </div>
                  
                  <div className="text-sm">
                    <span className="text-muted-foreground">Next run:</span>{' '}
                    <span className="font-medium">{formatNextRun(report.nextRun)}</span>
                    {report.lastRun && (
                      <>
                        {' • '}
                        <span className="text-muted-foreground">
                          Last run: {format(report.lastRun, 'MMM dd, HH:mm')}
                        </span>
                      </>
                    )}
                    {' • '}
                    <span className="text-muted-foreground">
                      {report.runCount} total runs
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 mr-4">
                    <Label htmlFor={`active-${report.id}`} className="text-sm">
                      Active
                    </Label>
                    <Switch
                      id={`active-${report.id}`}
                      checked={report.isActive}
                      onCheckedChange={(checked) => onToggle(report.id, checked)}
                    />
                  </div>
                  
                  <Button size="sm" variant="outline" onClick={() => onRunNow(report.id)}>
                    <Play className="h-3 w-3 mr-1" />
                    Run Now
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onEdit(report.id)}>
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onDelete(report.id)}>
                    <Trash2 className="h-3 w-3 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}